/* Per-conversation cache for MessageRoom.
 *
 * Rationale: entering a conversation from /messages always waits for
 *   1) GET /messaging/conversations/:id        (~90-120 ms)
 *   2) GET /messaging/conversations/:id/messages?limit=80  (~90-120 ms)
 *
 * Two problems the cache solves:
 *   (a) Users toggling between two chats pay this full ~200 ms each hop.
 *   (b) The conversation metadata (title, avatar, member_profiles) is
 *       already in the shared conversationsStore — we can display it
 *       INSTANTLY while messages load in parallel.
 *
 * TTL is deliberately generous (5 min) because the SSE stream keeps our
 * cached messages fresh via `appendMessage` in useRoomData.
 *
 * NOT a replacement for the API — every mount still hits the network
 * (staleness safety), but the UI paints INSTANTLY from the cache and
 * silently reconciles when the fresh response returns.
 */
const TTL_MS = 5 * 60 * 1000;

const _rooms = new Map(); // convId → { conv, messages, reads, cachedAt }

export function getCachedRoom(convId) {
  const entry = _rooms.get(convId);
  if (!entry) return null;
  if (Date.now() - entry.cachedAt > TTL_MS) {
    _rooms.delete(convId);
    return null;
  }
  return entry;
}

export function setCachedRoom(convId, { conv, messages, reads }) {
  _rooms.set(convId, {
    conv: conv || _rooms.get(convId)?.conv || null,
    messages: messages || _rooms.get(convId)?.messages || [],
    reads: reads || _rooms.get(convId)?.reads || null,
    cachedAt: Date.now(),
  });
}

// Called by SSE handler on message:new so background tab-switches
// come back to a fully up-to-date list.
export function appendCachedMessage(convId, msg) {
  const entry = _rooms.get(convId);
  if (!entry) return;
  if (entry.messages.some((x) => x.id === msg.id)) return;
  entry.messages = [...entry.messages, msg];
  entry.cachedAt = Date.now();
}

// Swap an optimistic (tmp_*) message for the server-canonical one. If the
// canonical is already present (SSE beat the POST response), just drop
// the temp copy so the list stays clean. Preserves the temp's original
// index so rapid-fire out-of-order responses don't re-order the list.
export function replaceCachedMessage(convId, tempId, real) {
  const entry = _rooms.get(convId);
  if (!entry) return;
  const idx = entry.messages.findIndex((x) => x.id === tempId);
  const hasCanonical = entry.messages.some((x) => x.id === real.id);
  if (idx === -1) {
    if (!hasCanonical) entry.messages = [...entry.messages, real];
  } else if (hasCanonical) {
    entry.messages = entry.messages.filter((x) => x.id !== tempId);
  } else {
    const next = [...entry.messages];
    next[idx] = real;
    entry.messages = next;
  }
  entry.cachedAt = Date.now();
}

// Remove a specific message from the cache — used when an optimistic
// send fails and we need to roll back.
export function removeCachedMessage(convId, msgId) {
  const entry = _rooms.get(convId);
  if (!entry) return;
  entry.messages = entry.messages.filter((x) => x.id !== msgId);
  entry.cachedAt = Date.now();
}

export function invalidateRoom(convId) {
  _rooms.delete(convId);
}
